/**
 * AdminCampDetailPage — dedicated detail view for a single Camp (Week).
 *
 * Replaces the limited "expand accordion card" UX on /admin/camps with a
 * full tabbed page that surfaces everything an admin needs per camp:
 *
 *   • Overview     — hero stats (enrolled / capacity / revenue / cancellations)
 *   • Sections     — per-location cards with one-click Roster drawer
 *   • Schedule     — day × location matrix with override indicator
 *   • Pricing      — bundle matrix editable per section with Live Preview
 *   • Enrollments  — server-filtered flat table (scoped to this camp)
 *   • Course Days  — per-day course detail (description, kit, thumbnail)
 *
 * Reuses existing endpoints — no backend changes required here.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { formatTime } from '../../utils/dateFormat';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  ArrowLeft, Tent, MapPin, Users, Calendar, DollarSign, Clock,
  Loader2, TrendingUp, AlertCircle, Mail, Phone, Eye, CheckCircle2, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDate } from '../../utils/dateFormat';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const STATUS_STYLES = {
  confirmed: 'bg-emerald-100 text-emerald-700',
  completed: 'bg-blue-100 text-blue-700',
  pending: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-rose-100 text-rose-700',
  waitlisted: 'bg-purple-100 text-purple-700',
};

function StatCard({ icon: Icon, label, value, hint, color = 'text-primary' }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            {hint && <p className="text-[11px] text-muted-foreground mt-0.5">{hint}</p>}
          </div>
          <Icon className={`h-8 w-8 ${color} opacity-70`} />
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminCampDetailPage() {
  const { campId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [camp, setCamp] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  // Roster drawer (Sections tab)
  const [rosterOpen, setRosterOpen] = useState(false);
  const [rosterSection, setRosterSection] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [campRes, enrRes] = await Promise.all([
        api.get(`/camps/${campId}/`),
        api.get(`/enrollments/?camp_id=${campId}&page_size=500`),
      ]);
      setCamp(campRes.data);
      setEnrollments(enrRes.data?.results || []);
    } catch {
      toast.error('Failed to load camp');
    } finally {
      setLoading(false);
    }
  }, [api, campId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const stats = useMemo(() => {
    if (!camp) return null;
    const sections = camp.sections || [];
    const totalCap = sections.reduce((s, x) => s + (x.capacity || 0), 0);
    const totalEnrolled = sections.reduce((s, x) => s + (x.enrolled_count || 0), 0);
    const utilisation = totalCap ? Math.round((totalEnrolled / totalCap) * 100) : 0;
    const paid = enrollments.filter(e => ['confirmed', 'completed'].includes(e.status));
    const cancelled = enrollments.filter(e => e.status === 'cancelled');
    const revenue = paid.reduce((s, e) => {
      const amt = parseFloat(e.payment?.amount_total ?? e.payment?.amount ?? 0);
      return s + (Number.isFinite(amt) ? amt : 0);
    }, 0);
    return { totalCap, totalEnrolled, utilisation, cancelled: cancelled.length, revenue, paidCount: paid.length };
  }, [camp, enrollments]);

  const rosterFor = (sectionId) => enrollments.filter(
    e => e.section === sectionId || e.camp_section === sectionId
  );

  const openRoster = (sec) => {
    setRosterSection(sec);
    setRosterOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!camp) {
    return (
      <div className="text-center py-16">
        <AlertCircle className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
        <p className="text-muted-foreground mb-4">Camp not found</p>
        <Button onClick={() => navigate('/admin/camps')}>Back to Camps</Button>
      </div>
    );
  }

  const sections = camp.sections || [];
  const days = camp.days || [];

  return (
    <div className="space-y-5" data-testid="admin-camp-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/camps')} data-testid="back-to-camps-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> All camps
          </Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Tent className="h-5 w-5 text-primary" />
              <h1 className="font-heading text-2xl font-bold">{camp.name}</h1>
              <Badge className={camp.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}>
                {camp.active ? 'Active' : 'Inactive'}
              </Badge>
              {camp.grade_bands?.length > 0 && (
                <Badge variant="outline" className="capitalize">{camp.grade_bands.join(', ')}</Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{camp.description}</p>
          </div>
        </div>
      </div>

      {/* Stat strip */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Users} label="Enrolled" value={`${stats.totalEnrolled}/${stats.totalCap}`} hint={`${stats.utilisation}% utilisation`} color="text-emerald-600" />
          <StatCard icon={MapPin} label="Locations" value={sections.length} color="text-blue-600" />
          <StatCard icon={DollarSign} label="Revenue" value={`$${stats.revenue.toFixed(0)}`} hint={`${stats.paidCount} paid seats`} color="text-amber-600" />
          <StatCard icon={XCircle} label="Cancellations" value={stats.cancelled} color={stats.cancelled > 0 ? 'text-rose-600' : 'text-muted-foreground'} />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-5 max-w-3xl">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-sections">Sections · {sections.length}</TabsTrigger>
          <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
          <TabsTrigger value="enrollments" data-testid="tab-enrollments">Enrollments · {enrollments.length}</TabsTrigger>
          <TabsTrigger value="days" data-testid="tab-days">Course Days · {days.length}</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="font-semibold">Quick actions</h3>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => setTab('sections')}>
                  <MapPin className="h-3 w-3 mr-1" /> Manage sections
                </Button>
                <Button size="sm" variant="outline" onClick={() => setTab('enrollments')}>
                  <Users className="h-3 w-3 mr-1" /> View {enrollments.length} enrollments
                </Button>
                <Link to="/admin/camps/schedule"><Button size="sm" variant="outline"><Calendar className="h-3 w-3 mr-1" /> Full schedule matrix</Button></Link>
                <Link to={`/camps/${camp.id}`} target="_blank"><Button size="sm" variant="outline"><Eye className="h-3 w-3 mr-1" /> View public page</Button></Link>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5">
              <h3 className="font-semibold mb-3">Latest enrollments</h3>
              {enrollments.length === 0 ? (
                <p className="text-sm text-muted-foreground">No enrollments yet.</p>
              ) : (
                <div className="space-y-2">
                  {enrollments.slice(0, 5).map(e => (
                    <div key={e.id} className="flex items-center gap-3 py-2 border-b last:border-0 text-sm">
                      <Badge className={STATUS_STYLES[e.status] || ''}>{e.status}</Badge>
                      <span className="font-medium">{e.learner_name || '—'}</span>
                      <span className="text-muted-foreground">Grade {e.learner_grade || '?'}</span>
                      <span className="ml-auto text-muted-foreground text-xs">{e.section_location} · {formatLocalDate(e.section_start_date)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SECTIONS */}
        <TabsContent value="sections" className="mt-4 space-y-3">
          {sections.length === 0 && (
            <p className="text-sm text-muted-foreground">No sections. Add one from the Camps list.</p>
          )}
          {sections.map(sec => {
            const roster = rosterFor(sec.id);
            const util = sec.capacity ? Math.round(((sec.enrolled_count || 0) / sec.capacity) * 100) : 0;
            return (
              <Card key={sec.id} data-testid={`section-card-${sec.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-[260px] space-y-1">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <h4 className="font-semibold">{sec.location_name}</h4>
                        {!sec.active && <Badge variant="outline">Inactive</Badge>}
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                        {sec.start_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatLocalDate(sec.start_date)} — {formatLocalDate(sec.end_date)}</span>}
                        {sec.start_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {formatTime(sec.start_time)}–{formatTime(sec.end_time)}</span>}
                        {sec.instructor_name && <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {sec.instructor_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className={`text-xl font-bold ${sec.enrolled_count > 0 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                          {sec.enrolled_count || 0}/{sec.capacity}
                        </p>
                        <p className="text-[10px] text-muted-foreground">{util}% filled</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => openRoster(sec)} data-testid={`view-roster-${sec.id}`}>
                        <Users className="h-3 w-3 mr-1" /> Roster ({roster.length})
                      </Button>
                    </div>
                  </div>
                  {/* Pricing row */}
                  <div className="mt-3 pt-3 border-t grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                    <div><span className="text-muted-foreground">Full camp</span><br/><span className="font-semibold">${sec.price_full_camp || '—'}</span></div>
                    <div><span className="text-muted-foreground">Per day</span><br/><span className="font-semibold">${sec.price_per_day || '—'}</span></div>
                    <div><span className="text-muted-foreground">AM</span><br/><span className="font-semibold">${sec.price_am_session || '—'}</span></div>
                    <div><span className="text-muted-foreground">5-Day Half</span><br/><span className="font-semibold">${sec.price_5_day_halfday || '—'}</span></div>
                    <div><span className="text-muted-foreground">4-Day Half</span><br/><span className="font-semibold">${sec.price_4_day_halfday || '—'}</span></div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* SCHEDULE */}
        <TabsContent value="schedule" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Day</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead className="w-24">Category</TableHead>
                    {sections.map(sec => (
                      <TableHead key={sec.id} className="text-center w-28 text-[10px]">
                        {sec.location_name?.replace('Ready Set STEM - ', '').replace(' Junior High School', ' JH')}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {days.map(day => (
                    <TableRow key={day.id}>
                      <TableCell><Badge variant="outline">{DAY_NAMES[day.day_number - 1]}</Badge></TableCell>
                      <TableCell className="font-medium">{day.course_title}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize text-[10px]">{day.course_category || '—'}</Badge></TableCell>
                      {sections.map(sec => {
                        const cellEnrs = rosterFor(sec.id).length;
                        return (
                          <TableCell key={sec.id} className="text-center">
                            <span className="text-xs font-medium text-emerald-700">{cellEnrs || '·'}</span>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ENROLLMENTS */}
        <TabsContent value="enrollments" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {enrollments.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground">
                  <Users className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No enrollments for this camp yet.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parent</TableHead>
                      <TableHead>Learner</TableHead>
                      <TableHead>Sessions</TableHead>
                      <TableHead>Paid</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enrollments.map(e => (
                      <TableRow key={e.id} data-testid={`enr-row-${e.id}`}>
                        <TableCell>
                          <div className="text-sm">{e.parent_name || '—'}</div>
                          <div className="text-[11px] text-muted-foreground">{e.parent_email}</div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm font-medium">{e.learner_name}</div>
                          <div className="text-[11px] text-muted-foreground">Grade {e.learner_grade || '?'}</div>
                        </TableCell>
                        <TableCell className="text-xs">{e.item_type || '—'}</TableCell>
                        <TableCell className="text-sm">${e.payment?.amount_total ?? e.payment?.amount ?? '0.00'}</TableCell>
                        <TableCell><Badge className={STATUS_STYLES[e.status] || ''}>{e.status}</Badge></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COURSE DAYS */}
        <TabsContent value="days" className="mt-4 space-y-3">
          {days.map(day => (
            <Card key={day.id}>
              <CardContent className="p-4 flex gap-4">
                {day.thumbnail_url && (
                  <img src={day.thumbnail_url} alt="" className="w-24 h-24 rounded object-cover shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-primary text-primary-foreground">Day {day.day_number} · {DAY_NAMES[day.day_number - 1]}</Badge>
                    <h4 className="font-semibold">{day.course_title}</h4>
                    <Badge variant="outline" className="capitalize text-[10px]">{day.course_category}</Badge>
                    <span className="text-xs text-muted-foreground ml-auto">${day.course_price} · {day.duration_minutes} min</span>
                  </div>
                  {day.course_description && (
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{day.course_description}</p>
                  )}
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {day.has_kit && <Badge variant="outline" className="text-[10px]">Kit: {day.kit_name}</Badge>}
                    {day.kit_required && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Kit required</Badge>}
                    {day.learning_outcomes?.length > 0 && (
                      <span className="text-[11px] text-muted-foreground">{day.learning_outcomes.length} learning outcomes</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      {/* Roster drawer */}
      <Dialog open={rosterOpen} onOpenChange={setRosterOpen}>
        <DialogContent className="max-w-2xl" data-testid="roster-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" /> Roster — {rosterSection?.location_name}
            </DialogTitle>
          </DialogHeader>
          {rosterSection && (() => {
            const roster = rosterFor(rosterSection.id);
            if (roster.length === 0) {
              return <div className="py-8 text-center text-muted-foreground">No enrollments yet for this location.</div>;
            }
            return (
              <div className="max-h-[60vh] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Learner</TableHead>
                      <TableHead>Parent</TableHead>
                      <TableHead>Grade</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {roster.map(e => (
                      <TableRow key={e.id}>
                        <TableCell className="font-medium text-sm">{e.learner_name}</TableCell>
                        <TableCell>
                          <div className="text-sm">{e.parent_name}</div>
                          <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                            {e.parent_email && <><Mail className="h-3 w-3" /> {e.parent_email}</>}
                            {e.parent_phone && <><Phone className="h-3 w-3 ml-1" /> {e.parent_phone}</>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{e.learner_grade || '—'}</TableCell>
                        <TableCell><Badge className={STATUS_STYLES[e.status] || ''}>{e.status}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
